import React, { useState, useMemo, useCallback } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Textarea,
  useToast,
  VStack,
  Text,
  Flex,
  Icon,
  Checkbox,
  Select,
  Divider,
  Grid,
  GridItem,
  HStack,
  useColorModeValue,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  useDisclosure,
} from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  FaArrowLeft,
  FaLock,
  FaUnlock,
  FaDatabase,
  FaRobot,
  FaPlus,
} from 'react-icons/fa'

import { dataService } from '../services/api'
import SymbolData from '../constants/SymbolData'
import { SymbolConfigRow } from '../components/SymbolConfigRow'
import { FeatureConfig } from '../components/FeatureConfig'
import { BatchImportModal } from '../components/BatchImportModal'

interface SymbolEntry {
  id: string
  selectedSymbol: string[]
  alias: string
}

interface FeatureEntry {
  name: string
  format: string
  description: string
}

const DataUploadPage = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_asset: '',
    is_encrypted: false,
    update_frequency: 1000,
    type: 'Data',
  })

  // State for symbols and features
  const [symbols, setSymbols] = useState<SymbolEntry[]>([{
    id: crypto.randomUUID(),
    selectedSymbol: [],
    alias: ''
  }])
  const [features, setFeatures] = useState<FeatureEntry[]>([{
    name: 'default',
    format: 'double',
    description: ''
  }])

  // State for batch import
  const {
    isOpen: isBatchImportOpen,
    onOpen: onBatchImportOpen,
    onClose: onBatchImportClose
  } = useDisclosure()

  // State for loading and response
  const [isLoading, setIsLoading] = useState(false)

  // State for form submission
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Pre-process SymbolData
  const groupedSymbolData = useMemo(() => {
    const map: Record<string, string[]> = {}
    SymbolData.forEach(item => {
      const parts = item.split('|')
      if (parts.length === 2) {
        const [ex, sym] = parts
        if (!map[ex]) map[ex] = []
        map[ex].push(sym)
      }
    })
    return map
  }, [])

  // Toast for notifications
  const toast = useToast()

  const navigate = useNavigate()
  const location = useLocation()

  // Colors based on theme
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  // Handle input changes
  const handleInputChange = useCallback((e: any) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleAddSymbol = useCallback(() => {
    setSymbols(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        selectedSymbol: [],
        alias: ''
      }
    ])
  }, [])

  const handleImportSymbols = useCallback((text: string) => {
    // Split by comma
    const items = text.split(',').map(s => s.trim()).filter(Boolean)

    const newSymbols = items.map((item) => ({
      id: crypto.randomUUID(),
      selectedSymbol: [item], // Assuming the user provides "Exchange_Symbol" or just "Symbol"
      alias: '',
    }))

    // Overwrite existing symbols
    setSymbols(newSymbols)
  }, [])

  const handleRemoveSymbol = useCallback((id: string) => {
    setSymbols(prev => prev.filter(s => s.id !== id))
  }, [])

  const handleSymbolChange = useCallback((id: string, field: 'selectedSymbol' | 'alias', value: any) => {
    setSymbols(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: value } : s
    ))
  }, [])

  const handleFeaturesChange = useCallback((newFeatures: FeatureEntry[]) => {
    setFeatures(newFeatures)
  }, [])

  const handleBack = () => {
    // Check if we can go back in history
    if (location.key !== 'default') {
      navigate(-1) // Navigate to previous page in history
    } else {
      // If there's no history (e.g., opened in new tab), go to the records page
      navigate('/data')
    }
  }

  // Handle checkbox changes
  const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }))
  }, [])

  // Handle number input changes
  const handleNumberChange = useCallback((name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  // Validate config before submission
  const validateConfig = useCallback((): { isValid: boolean; errorMessage?: string } => {
    // Check: at least one symbol
    if (symbols.length === 0) {
      return { isValid: false, errorMessage: 'Please add at least one symbol.' }
    }

    // Check: each symbol must be selected
    const invalidSymbols = symbols.filter(s => s.selectedSymbol.length === 0)
    if (invalidSymbols.length > 0) {
      return { isValid: false, errorMessage: 'Please select or enter a symbol for all entries.' }
    }

    // Check: no duplicate symbols
    const allSymbolValues = symbols.map(s => s.selectedSymbol[0])
    const uniqueSymbols = new Set(allSymbolValues)
    if (uniqueSymbols.size !== allSymbolValues.length) {
      return { isValid: false, errorMessage: 'Duplicate symbols are not allowed.' }
    }

    // Check: each feature must have a name
    const emptyFeatureNames = features.filter(f => !f.name.trim())
    if (emptyFeatureNames.length > 0) {
      return { isValid: false, errorMessage: 'All features must have a name.' }
    }

    // Check: no duplicate feature names
    const featureNames = features.map(f => f.name.trim())
    const uniqueFeatureNames = new Set(featureNames)
    if (uniqueFeatureNames.size !== featureNames.length) {
      return { isValid: false, errorMessage: 'Duplicate feature names are not allowed.' }
    }

    return { isValid: true }
  }, [symbols, features])

  // Handle form submission
  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setIsSubmitted(true)

    // Validate
    const validation = validateConfig()
    if (!validation.isValid) {
      toast({
        title: 'Validation Error',
        description: validation.errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      // Build data array for V2 API
      // Combine each symbol with the global features
      const data = symbols.map(sym => {
        // symbol and target_asset use the same value (the selected symbol string)
        const symbolValue = sym.selectedSymbol[0] || ''
        // If alias is empty, use the symbol value
        const aliasValue = sym.alias && sym.alias.trim() ? sym.alias.trim() : symbolValue

        return {
          symbol: aliasValue,
          target_asset: symbolValue,
          features: features.map(f => ({
            name: f.name,
            description: f.description,
            dataType: f.format, // map format to dataType
          }))
        }
      })

      const response = await dataService.createDataRecordV2({
        name: formData.name,
        description: formData.description,
        is_encrypted: formData.is_encrypted,
        type: formData.type,
        update_frequency: formData.update_frequency,
        data,
      })

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to create data record')
      }

      toast({
        title: 'Success',
        description: response.message || 'Data record created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      // Reset form after successful submission
      setFormData({
        name: '',
        description: '',
        target_asset: '',
        is_encrypted: false,
        update_frequency: 1000,
        type: 'Data',
      })
      setSymbols([])
      setFeatures([])

      handleBack()
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxWidth="1200px" mx="auto">
      <Button
        leftIcon={<Icon as={FaArrowLeft} />}
        mb={4}
        onClick={handleBack}
        variant="outline"
        size="md"
        borderRadius="md"
      >
        Back
      </Button>

      <Heading size="lg" fontWeight="bold" textAlign={'center'} mb={4}>
        Create Record
      </Heading>

      <Card
        variant="outline"
        bg={cardBg}
        borderColor={borderColor}
        borderRadius="lg"
        shadow="md"
        overflow="hidden"
      >
        <CardBody p={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr 2fr' }} gap={6}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Name</FormLabel>
                    <Input
                      name="name"
                      placeholder="E.g., Bitcoin Price Analysis"
                      value={formData.name}
                      onChange={handleInputChange}
                      size="md"
                      borderRadius="md"
                      focusBorderColor="blue.400"
                    />
                  </FormControl>
                </GridItem>

                {/* Type Selector Field */}
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Record Type</FormLabel>
                    <InputGroup size="md">
                      <InputLeftAddon>
                        <Icon
                          as={formData.type === 'Data' ? FaDatabase : FaRobot}
                        />
                      </InputLeftAddon>
                      <Select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        borderRadius="md"
                        focusBorderColor="blue.400"
                      >
                        <option value="Data">Data</option>
                        <option value="Alpha">Alpha</option>
                      </Select>
                    </InputGroup>
                  </FormControl>
                </GridItem>

                {/* Update Frequency Field */}
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium">Update Frequency</FormLabel>
                    <Box flex="1">
                      <InputGroup size="md">
                        <Input
                          type="number"
                          min={1}
                          value={formData.update_frequency}
                          onChange={(e) =>
                            handleNumberChange(
                              'update_frequency',
                              parseInt(e.target.value) || 1000
                            )
                          }
                          borderRadius="md"
                          focusBorderColor="blue.400"
                        />
                        <InputRightAddon>ms</InputRightAddon>
                      </InputGroup>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Update interval in milliseconds
                      </Text>
                    </Box>
                  </FormControl>
                </GridItem>
              </Grid>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">Description</FormLabel>
                <Textarea
                  name="description"
                  placeholder="Describe the purpose of this data record"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  size="md"
                  borderRadius="md"
                  focusBorderColor="blue.400"
                  resize="vertical"
                />
              </FormControl>
              <Divider my={2} />

              <Heading size="sm" fontWeight="semibold" color="gray.600" mb={2}>
                Config Symbols
              </Heading>

              {/* Symbol Configuration Section */}
              <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50" position="relative">
                <Flex gap={4} alignItems="center" mb={4}>
                  <Button
                    leftIcon={<Icon as={FaPlus} />}
                    onClick={handleAddSymbol}
                    size="md"
                    variant="outline"
                    colorScheme="blue"
                    flexShrink={0}
                  >
                    Add Symbol
                  </Button>

                  <Button
                    rightIcon={<Icon as={FaPlus} />}
                    onClick={onBatchImportOpen}
                    size="md"
                    variant="outline"
                    colorScheme="teal"
                    flexShrink={0}
                  >
                    Batch Import
                  </Button>
                </Flex>

                {symbols.length > 0 && (
                  <VStack spacing={4} align="stretch">
                    {symbols.map((sym) => (
                      <SymbolConfigRow
                        key={sym.id}
                        id={sym.id}
                        groupedSymbolData={groupedSymbolData}
                        selectedSymbol={sym.selectedSymbol}
                        alias={sym.alias}
                        onSymbolChange={handleSymbolChange}
                        onRemove={handleRemoveSymbol}
                        isSubmitted={isSubmitted}
                      />
                    ))}
                  </VStack>
                )}
              </Box>

              <Divider my={2} />
              <Heading size="sm" fontWeight="semibold" color="gray.600" mb={2}>
                Config Features
              </Heading>

              <FeatureConfig
                features={features}
                onFeaturesChange={handleFeaturesChange}
              />

              {/* Encryption Checkbox Field */}
              <FormControl>
                <HStack spacing={2} mt={2}>
                  <Checkbox
                    name="is_encrypted"
                    isChecked={formData.is_encrypted}
                    onChange={handleCheckboxChange}
                    colorScheme="blue"
                    size="lg"
                  />
                  <Flex align="center">
                    <Icon
                      as={formData.is_encrypted ? FaLock : FaUnlock}
                      color={formData.is_encrypted ? 'green.500' : 'gray.400'}
                      mr={2}
                    />
                    <Text fontWeight="medium">Enable Encryption</Text>
                  </Flex>
                </HStack>
              </FormControl>

              <Divider my={4} />

              <Flex justify="flex-end">
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isLoading}
                  loadingText="Creating Record"
                  size="lg"
                  px={8}
                  borderRadius="md"
                  shadow="md"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                  transition="all 0.2s"
                >
                  Create Record
                </Button>
              </Flex>
            </VStack>
          </form>
        </CardBody>
      </Card>

      {/* Batch Import Symbols Modal */}
      <BatchImportModal
        isOpen={isBatchImportOpen}
        onClose={onBatchImportClose}
        onImport={handleImportSymbols}
        title="Batch Import Symbols"
        placeholder="e.g. Binance_BTCUSDT, Binance_ETHUSDT"
      />

    </Box>
  )
}

export default DataUploadPage
